import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PopularCategoryIcon } from '../PopularCategoryIcons';
import { getEventCategoryVisual } from '../../../config/eventCategoryVisuals';
import SingleEvent from '../../../classes/events/SingleEvent';
import { getProfileCardDisplayName } from '../../../utils/profileDisplayName';
import {
  ProfileAchievementsCalendarLinkIcon,
  ProfileStatClockIcon,
  ProfileCardMapPinIcon,
} from '../../profile/ProfileActivityIcons';
import './EventInfoWindow.css';

const markerPopupHostCache = new Map();

function MarkerPopupPriceIcon({ size = 25, className = 'marker-popup-tag-icon' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
    >
      <g stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5.2 9.2 11.8 4l7 5.2v8.1a2.7 2.7 0 0 1-2.7 2.7H7.9a2.7 2.7 0 0 1-2.7-2.7V9.2Z" />
        <path d="M9 10.4h5.1a2.1 2.1 0 0 1 0 4.2H9" />
        <path d="M9 8.5v7.9M15.2 16.4H9" />
      </g>
    </svg>
  );
}

// Calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatShortAddress(location) {
  if (!location || typeof location !== 'string') {
    return null;
  }

  const parts = location
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2 && /^\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?$/.test(parts[0])) {
    return `${parts[1]} ${parts[0]}`;
  }

  const streetPart = parts.find((part) => /\b(?:ul\.?|aleja|al\.|plac|pl\.)\b/i.test(part)) || parts[0];
  return streetPart || null;
}

function formatStartDateTooltip(eventDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(eventDate);
  startDate.setHours(0, 0, 0, 0);

  const daysUntil = Math.round((startDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

  if (daysUntil === 0) {
    return 'Event zaczyna się dzisiaj';
  }
  if (daysUntil === 1) {
    return 'Event zaczyna się jutro';
  }
  if (daysUntil > 1) {
    return `Event zaczyna się za ${daysUntil} dni`;
  }
  return 'Event już się rozpoczął';
}

function formatDurationTooltip(durationMinutes) {
  const totalMinutes = Number(durationMinutes);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return null;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} godz.`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }

  return parts.join(' ');
}

function EventInfoWindow({ event, onClose, onViewDetails, onViewProfile, userLocation }) {
  const { t, i18n } = useTranslation(['discover', 'events']);
  const [host, setHost] = useState(event?.host || null);

  useEffect(() => {
    let cancelled = false;
    const hostId = event?.host_id;

    if (event?.host) {
      setHost(event.host);
      return () => {
        cancelled = true;
      };
    }

    if (!hostId) {
      setHost(null);
      return () => {
        cancelled = true;
      };
    }

    if (markerPopupHostCache.has(hostId)) {
      setHost(markerPopupHostCache.get(hostId));
      return () => {
        cancelled = true;
      };
    }

    SingleEvent.loadHost(hostId).then(({ data }) => {
      if (cancelled) return;
      markerPopupHostCache.set(hostId, data || null);
      setHost(data || null);
    });

    return () => {
      cancelled = true;
    };
  }, [event?.host, event?.host_id]);

  if (!event || !event.coordinates) {
    return null;
  }

  // Calculate distance if user location is available
  let distance = null;
  if (userLocation && event.coordinates) {
    distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      event.coordinates.lat,
      event.coordinates.lng
    );
  }

  const categoryVisual = getEventCategoryVisual(event.event_type);

  // Formatowanie daty
  const eventDate = new Date(event.event_date + 'T00:00:00');
  const day = eventDate.getDate();
  const month = eventDate
    .toLocaleDateString(i18n.language || 'pl-PL', { month: 'short' })
    .toUpperCase();
  const time = event.start_time?.slice(0, 5) || '-';
  const categoryLabel = t(`discover.popularCategories.names.${categoryVisual.key}`, {
    defaultValue: categoryVisual.key,
  });
  const detailsLabel = t('discover.viewDetails', 'Szczegóły');
  const shortAddress = formatShortAddress(event.location);
  const dateTooltip = formatStartDateTooltip(eventDate);
  const durationText = formatDurationTooltip(event.duration_minutes);
  const timeTooltip = durationText
    ? `Start: ${time} • Czas trwania: ${durationText}`
    : `Start: ${time}`;
  const participationCost =
    !event.is_free && event.total_cost && event.max_participants
      ? `${(parseFloat(event.total_cost) / event.max_participants).toFixed(2)} zł`
      : null;
  const participationLabel = event.is_free
    ? 'Wstęp bezpłatny'
    : participationCost
      ? 'Koszt uczestnictwa'
      : null;
  const hostDisplayName = getProfileCardDisplayName(host, {
    allowRealName: true,
    nameNotSetLabel: t('events.details.unknownUser', 'Nieznany użytkownik'),
  });
  const handleHostClick = (e) => {
    e.stopPropagation();
    if (host?.id) {
      onViewProfile?.(host.id);
    }
  };

  // Formatowanie dystansu
  const distanceText = distance !== null 
    ? (distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`)
    : null;

  return (
    <div
      key={event.id}
      className="marker-popup"
      style={{
        '--marker-accent': categoryVisual.color,
        '--marker-accent-soft': categoryVisual.softColor,
      }}
    >
      <div className="marker-popup-atmosphere" aria-hidden="true" />

      <div className="marker-popup-top">
        <div className="marker-popup-category">
          <PopularCategoryIcon variant={categoryVisual.key} className="marker-popup-category-bg" aria-hidden />
          <span className="marker-popup-category-core" aria-hidden="true" />
        </div>
      </div>

      <div className="marker-popup-copy">
        <span className="marker-popup-kicker">{categoryLabel}</span>
        <h3 className="marker-popup-title">{event.title}</h3>
      </div>

      <div className="marker-popup-meta">
        <div className="marker-popup-datetime-line" title={`${dateTooltip} • ${timeTooltip}`}>
          <div className="marker-popup-date marker-popup-date--meta marker-popup-date--inline" title={dateTooltip}>
            <ProfileAchievementsCalendarLinkIcon size={25} className="marker-popup-tag-icon" />
            <span className="marker-popup-day">{day}</span>
            <span className="marker-popup-month">{month}</span>
          </div>
          <span className="marker-popup-tag marker-popup-time-slot" title={timeTooltip}>
            <ProfileStatClockIcon size={25} className="marker-popup-tag-icon" />
            {time}
          </span>
        </div>
        {distanceText && (
          <span className="marker-popup-tag" title={t('discover.map.markerPopup.distance')}>
            <ProfileCardMapPinIcon size={25} className="marker-popup-tag-icon" />
            {distanceText}
          </span>
        )}
        {shortAddress && (
          <span className="marker-popup-tag marker-popup-address" title={event.location}>
            <ProfileCardMapPinIcon size={25} className="marker-popup-tag-icon" />
            {shortAddress}
          </span>
        )}
      </div>

      {(hostDisplayName || participationLabel) && (
        <div
          className={`marker-popup-host${!hostDisplayName && participationLabel ? ' marker-popup-host--price-only' : ''}`}
        >
          <div className="marker-popup-host-info">
            {hostDisplayName ? (
              <>
                <span className="marker-popup-host-label">Hostowany przez:</span>
                {host?.id ? (
                  <button type="button" className="marker-popup-host-name" onClick={handleHostClick}>
                    {hostDisplayName}
                  </button>
                ) : (
                  <span className="marker-popup-host-name">{hostDisplayName}</span>
                )}
              </>
            ) : null}
          </div>
          {participationLabel && (
            <div
              className={`marker-popup-host-price marker-popup-tag ${event.is_free ? 'marker-popup-free' : 'marker-popup-paid'}`}
              title={participationCost ? `${participationLabel} ${participationCost}` : participationLabel}
            >
              <MarkerPopupPriceIcon size={25} className="marker-popup-tag-icon" />
              <span className="marker-popup-host-price-text">
                {participationLabel}
                {participationCost && <strong className="marker-popup-price-value">{participationCost}</strong>}
              </span>
            </div>
          )}
        </div>
      )}

      <button
        className="marker-popup-btn"
        onClick={() => onViewDetails(event.id)}
      >
        {detailsLabel}
      </button>
    </div>
  );
}

export default EventInfoWindow;
